import {
    dateCompare,
    DateFormatHelper,
    DetailItem,
    DetailSection,
    DOMAIN_TYPES,
    formatName,
} from "@/Shared";
import { BirthWishModel } from '../BirthWishModel';
import { AuditViewModel } from "@/Shared/Logic/ViewModels/AuditViewModel";
import { AuditModel } from "@/Shared/Logic/Models/AuditModel";
import { AccessRightModel } from "@/Domains/Auth";

export interface BirthWishVMParams {
    birthWish: BirthWishModel,
    accessRight: AccessRightModel,
    isDisplaying: boolean
}

/**
 * =====================================================
 * Class: BirthWishViewModel
 * =====================================================
 *
 * Description:
 * Provides a view model for BirthWishModel with enriched fields,
 * computed properties, audit info, and access-based UI configuration.
 *
 * Responsibilities:
 * - Format dates, names, and audit info for display
 * - Provide DetailSections for UI rendering
 * - Determine card CSS classes and headers based on display status
 * - Provide comparison method for sorting
 * 
 * Author: Samano CASTRE
 * Date: July 17, 2025
 */
export class BirthWishViewModel {

    // Basic identifiers and information
    public id!: number;
    public contactId!: number;

    public contactEmail!: string;
    public mailingYear!: number;
    public message!: string;

    // Formatted names for display
    public agent?: string;
    public contact?: string;

    // Audit and metadata
    public audit!: AuditViewModel;
    public creationDate!: string;
    public creatorName!: string;


    public birthday!: string;

    // Reference to the original model
    public model!: BirthWishModel;

    // UI display properties
    public cardClass!: string;
    public cardHeaderTitle!: string;
    public isDeleted!: boolean;

    /**
    * Constructs a BirthWishViewModel
    * @param params Parameters containing the model, access rights, and display state
    */
    public constructor(protected params: BirthWishVMParams) {
        this.initialize();
    }


    /**
    * Initializes properties based on the model and parameters
    */
    private initialize() {
        const { birthWish, isDisplaying } = this.params;

        // Enriched audit info for display and traceability
        this.audit = new AuditViewModel(birthWish as AuditModel);
        this.model = birthWish;

        // Main properties
        this.id = birthWish?.id ?? 0;
        this.contactId = birthWish?.contactId ?? 0;
        this.contactEmail = birthWish?.contactEmail ?? "";
        this.mailingYear = birthWish?.mailingYear ?? new Date().getFullYear();
        this.message = birthWish?.message ?? "";
        this.contact = formatName(birthWish?.contact?.person);
        this.creatorName = this.audit.creatorName;

        // Format dates for display
        this.birthday = DateFormatHelper.toLocaleDateString(birthWish.contact?.person.birthDate) ?? "";
        this.creationDate = this.audit.creationDate;

        // UI state and card configuration
        this.isDeleted = !!birthWish.deletedAt
        this.cardClass = isDisplaying ? 'active' : '';
        this.cardHeaderTitle = `Vœu N° ${this.id} - ${this.contact}`;
    }

    /**
     * Returns the card title for display
     */
    get title() {
        return `Vœu N° ${this.id} - ${this.contact}`;
    }

    /**
     * Builds all detail sections for UI display
     * - Main section: birth wish characteristics
     * - Audit section: only included if user has audit permissions
     */
    getAllSections = (): DetailSection[] => {
        const sections: DetailSection[] = [
            {
                sectionFn: this.getBirthWishSection,
                legend: 'Caractéristiques Générales',
                type: 'box'
            },
        ];

        if (this.params.accessRight.canAudit(DOMAIN_TYPES.BirthWish)) {
            sections.push({
                sectionFn: this.audit.getAuditTrail,
                legend: 'Traçabilité',
                type: 'box'
            });
        }
        return sections;
    }

    /**
    * Returns the main properties of the birth wish for a detail section
    */
    getBirthWishSection = () => {
        return [
            { label: "N° Vœu", value: this.id },
            { label: "Contact", value: this.contact ?? this.contactEmail },
            { label: "Date naiss.", value: this.birthday },
            { label: "Année", value: this.mailingYear },
            { label: "Message", value: this.message },
            { label: "Créée le", value: this.creationDate },
        ] as DetailItem[];
    }

    /**
    * Compares this birth wish with another for chronological sorting
    * @param other Another BirthWishViewModel to compare against
    * @returns Negative if this wish is older, positive if newer
    */
    compareTo(other: BirthWishViewModel): number {
        return dateCompare(this.model.createdAt ?? "", other.model.createdAt ?? "");
    }
}
